import { normalizeApproximateCostPlan } from '../api/approximateCostPlan';
import { normalizeGeorgiaUnitText } from '../api/constructionPriceGeorgiaUnit';
import { toUserFacingError } from '../utils/userFacingError';
import type {
  BtbBreakdownPublic, BtbTechnologyDetailPublic,
  BtbTechnologyMoneyRangePublic, ComparableCohortPublic,
  GeorgiaObservedEvidencePublic, GeorgiaObservedEvidenceItemPublic,
  UnifiedSourceCheckedPublic, UnifiedSourceResearchPublic,
} from '../api/constructionPriceEstimatorFunctions';

type RecordValue = Record<string, unknown>;
export const safeActionError = (value: unknown, _fallback: string): string =>
  toUserFacingError(value);
const record = (value: unknown): RecordValue | null => (
  value && typeof value === 'object' && !Array.isArray(value)
    ? value as RecordValue : null
);
const text = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const result = value.trim();
  return result && result.length <= 360
    && !/^[a-z][a-z0-9]*(?:_[a-z0-9]+)+$/i.test(result)
    ? result : null;
};
const date = (value: unknown): string | null => {
  const valueText = text(value);
  return valueText && /^\d{4}-\d{2}-\d{2}(?:T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})?)?$/.test(valueText)
    ? valueText : null;
};
const amount = (value: unknown, positive = false): string | number | null => {
  if (typeof value === 'number') {
    return Number.isFinite(value) && (!positive || value > 0) ? value : null;
  }
  return typeof value === 'string'
    && /^-?\d{1,12}(?:\.\d{1,6})?$/.test(value)
    && (!positive || Number(value) > 0) ? value : null;
};
const count = (value: unknown): number | null => (
  typeof value === 'number' && Number.isInteger(value) && value >= 0
    && value <= 100_000 ? value : null
);
const textArray = (value: unknown, limit = 8): string[] => (
  Array.isArray(value) ? value.slice(0, limit).map(text)
    .filter((item): item is string => Boolean(item)) : []
);

export function projectSourceLedger(
  value: unknown,
): UnifiedSourceCheckedPublic[] {
  return Array.isArray(value) ? value.slice(0, 20).flatMap((candidate) => {
    const source = record(candidate);
    const label = text(source?.label);
    const outcome = source?.outcome;
    const publicOutcome = outcome === 'consulted' || outcome === 'reused'
      || outcome === 'no_matching_evidence' || outcome === 'unavailable'
      || outcome === 'not_available' || outcome === 'not_applicable' ? outcome : null;
    return label ? [{ label,
      ...(text(source?.detail) ? { detail: text(source?.detail)! } : {}),
      ...(publicOutcome ? { outcome: publicOutcome } : {}) }] : [];
  }) : [];
}

export function projectGeorgiaEvidence(value: unknown): GeorgiaObservedEvidencePublic | null {
  const raw = record(value);
  if (!raw || raw.heading !== 'Georgia public-owner price evidence'
    || raw.scope !== 'Georgia public-owner observed installed-price evidence'
    || !['available', 'no_match', 'insufficient_evidence', 'not_configured'].includes(String(raw.status))) return null;
  const items: GeorgiaObservedEvidenceItemPublic[] = [];
  if (Array.isArray(raw.items)) raw.items.slice(0, 50).forEach((candidate) => {
    const item = record(candidate);
    if (!item) return;
    const work_item = text(item.work_item);
    const original_description = text(item.original_description);
    const unit = text(normalizeGeorgiaUnitText(item.unit));
    const quantity = amount(item.quantity, true);
    const unit_price = amount(item.unit_price);
    const extended_price = amount(item.extended_price);
    const price_date = date(item.price_date);
    const project_location = text(item.project_location);
    const source_reference = text(item.source_reference);
    if (!work_item || !original_description || !unit || quantity === null
      || unit_price === null || extended_price === null || !price_date
      || !project_location || !source_reference || item.currency !== 'USD'
      || item.source_label !== 'Approved Georgia public owner'
      || (item.evidence_status !== 'Awarded' && item.evidence_status !== 'Final paid')) return;
    items.push({ work_item, original_description, unit, quantity, unit_price,
      extended_price, currency: 'USD', price_date, project_location,
      source_label: 'Approved Georgia public owner', source_reference,
      evidence_status: item.evidence_status, caveats: textArray(item.caveats) });
  });
  if (raw.status === 'available' && !items.length) return null;
  if (raw.status !== 'available' && items.length) return null;
  return {
    status: raw.status as GeorgiaObservedEvidencePublic['status'],
    heading: 'Georgia public-owner price evidence',
    scope: 'Georgia public-owner observed installed-price evidence',
    ...(date(raw.as_of) ? { as_of: date(raw.as_of)! } : {}),
    items, warnings: textArray(raw.warnings),
  };
}

const btbRange = (value: unknown): BtbTechnologyMoneyRangePublic | null => {
  const raw = record(value);
  if (!raw) return null;
  const low = amount(raw.low);
  const median = amount(raw.median);
  const high = amount(raw.high);
  const unit_label = text(raw.unit_label);
  return low !== null && median !== null && high !== null && unit_label
    && Number(low) <= Number(median) && Number(median) <= Number(high)
    ? { low, median, high, unit_label } : null;
};

export function projectBtbEvidence(value: unknown): BtbBreakdownPublic | null {
  const raw = record(value);
  if (!raw || raw.heading !== 'Technology cost details'
    || raw.source_label !== 'DOE/NREL Buildings Technology Baseline (2024)'
    || raw.geography_label !== 'National' || raw.value_year_label !== '2023 dollars'
    || !['available', 'partial', 'no_matches', 'unavailable'].includes(String(raw.status))) return null;
  const items: BtbTechnologyDetailPublic[] = [];
  if (Array.isArray(raw.items)) raw.items.slice(0, 50).forEach((candidate) => {
    const item = record(candidate);
    if (!item) return;
    const project_item_label = text(item.project_item_label);
    const technology_label = text(item.technology_label);
    const retail_benchmark = btbRange(item.retail_benchmark);
    const installation_allowance = btbRange(item.installation_allowance);
    const attribution = text(item.attribution);
    const limitation = text(item.limitation);
    if (!project_item_label || !technology_label || !retail_benchmark
      || !installation_allowance || !attribution || !limitation
      || (item.building_segment !== 'Commercial' && item.building_segment !== 'Residential')
      || item.scenario_label !== 'Reference'
      || (item.value_basis_label !== 'Source-typical' && item.value_basis_label !== 'Project-scaled')
      || count(item.model_year) === null) return;
    items.push({ project_item_label, technology_label,
      building_segment: item.building_segment, model_year: item.model_year as number,
      scenario_label: 'Reference', value_basis_label: item.value_basis_label,
      retail_benchmark, installation_allowance, attribution, limitation,
      ...(text(item.end_use_label) ? { end_use_label: text(item.end_use_label)! } : {}),
      ...(text(item.fuel_label) ? { fuel_label: text(item.fuel_label)! } : {}),
      ...(text(item.typical_unit_label) ? { typical_unit_label: text(item.typical_unit_label)! } : {}),
      ...(btbRange(item.installed_total) ? { installed_total: btbRange(item.installed_total)! } : {}),
      ...(btbRange(item.installed_new_construction) ? { installed_new_construction: btbRange(item.installed_new_construction)! } : {}),
      ...(btbRange(item.installed_retrofit) ? { installed_retrofit: btbRange(item.installed_retrofit)! } : {}),
      ...(item.construction_scope_label === 'New construction' || item.construction_scope_label === 'Retrofit'
        ? { construction_scope_label: item.construction_scope_label } : {}),
      ...(item.range_quality_warning === 'Wide price range—review before relying on it.'
        ? { range_quality_warning: item.range_quality_warning } : {}),
      ...(item.negative_installation_warning === 'Source model produces a negative installation adjustment.'
        ? { negative_installation_warning: item.negative_installation_warning } : {}),
      ...(text(item.source_quality_summary) ? { source_quality_summary: text(item.source_quality_summary)! } : {}),
    });
  });
  if ((raw.status === 'available' || raw.status === 'partial') && !items.length) return null;
  return { status: raw.status as BtbBreakdownPublic['status'],
    heading: 'Technology cost details',
    source_label: 'DOE/NREL Buildings Technology Baseline (2024)',
    geography_label: 'National', value_year_label: '2023 dollars', items,
    ...(count(raw.unmatched_count) !== null
      ? { unmatched_count: count(raw.unmatched_count)! } : {}),
  };
}

function projectCohort(value: unknown): ComparableCohortPublic | null {
  const raw = record(value);
  if (!raw || !['tight_supported', 'broader_supported', 'insufficient_similarity',
    'context_only', 'source_unavailable'].includes(String(raw.status))) return null;
  const summary = text(raw.summary);
  const selected_count = count(raw.selected_count);
  if (!summary || selected_count === null) return null;
  const excluded_count = count(raw.excluded_count);
  return { status: raw.status as ComparableCohortPublic['status'], summary,
    selected_count,
    ...(excluded_count !== null ? { excluded_count } : {}),
    ...(['county', 'city', 'state'].includes(String(raw.geographic_tier))
      ? { geographic_tier: raw.geographic_tier as ComparableCohortPublic['geographic_tier'] } : {}),
  };
}

export function projectUnifiedResearch(value: unknown): UnifiedSourceResearchPublic | null {
  const raw = record(value);
  const recommendation = record(raw?.recommendation);
  if (!raw || raw.schema_version !== 'unified-source-research-v1'
    || !recommendation || recommendation.display_label !== 'Recommended planning price'
    || !['recommended', 'evidence_conflict', 'benchmark_only', 'context_only', 'insufficient']
      .includes(String(recommendation.outcome))) return null;
  const limitation = text(recommendation.limitation);
  if (!limitation) return null;
  const rawRange = record(recommendation.range);
  const low = amount(rawRange?.low, true);
  const median = amount(rawRange?.median, true);
  const high = amount(rawRange?.high, true);
  const unit_label = text(rawRange?.unit_label);
  const basis_label = text(rawRange?.basis_label);
  const pricing_date_label = text(rawRange?.pricing_date_label);
  const range = low !== null && median !== null && high !== null
    && unit_label && basis_label && pricing_date_label
    && Number(low) <= Number(median) && Number(median) <= Number(high)
    ? { low, median, high, unit_label, basis_label, pricing_date_label } : null;
  if (recommendation.outcome === 'recommended' && !range) return null;
  const drawer = record(raw.evidence_drawer);
  const lanes = Array.isArray(raw.lanes) ? raw.lanes.flatMap((value) => {
    const lane = record(value);
    const label = text(lane?.label);
    if (!label || !['pending', 'running', 'complete', 'reused', 'no_matching_evidence',
      'unavailable', 'failed', 'not_applicable'].includes(String(lane?.state))) return [];
    return [{ label, state: lane!.state as UnifiedSourceResearchPublic['lanes'][number]['state'],
      ...(text(lane?.message) ? { message: text(lane?.message)! } : {}),
      ...(count(lane?.evidence_count) !== null ? { evidence_count: count(lane?.evidence_count)! } : {}) }];
  }) : [];
  const sources_checked = projectSourceLedger(raw.sources_checked);
  const georgia = projectGeorgiaEvidence(raw.georgia_observed_evidence);
  const btb = projectBtbEvidence(raw.btb_breakdown);
  const cohort = projectCohort(raw.comparable_cohort);
  const shape = recommendation.display_shape;
  const countValue = count(recommendation.cohort_count);
  return {
    schema_version: 'unified-source-research-v1',
    recommendation: {
      outcome: recommendation.outcome as UnifiedSourceResearchPublic['recommendation']['outcome'],
      display_label: 'Recommended planning price', limitation,
      ...(range ? { range } : {}),
      ...(text(recommendation.primary_source_label)
        ? { primary_source_label: text(recommendation.primary_source_label)! } : {}),
      ...(text(recommendation.basis_label)
        ? { basis_label: text(recommendation.basis_label)! } : {}),
      ...(text(recommendation.applicability_label)
        ? { applicability_label: text(recommendation.applicability_label)! } : {}),
      ...(shape === 'single' || shape === 'range' || shape === 'distribution'
        ? { display_shape: shape } : {}),
      ...(countValue !== null ? { cohort_count: countValue } : {}),
      ...(recommendation.range_quality_warning === 'Wide price range—review before relying on it.'
        ? { range_quality_warning: recommendation.range_quality_warning } : {}),
    },
    evidence_drawer: {
      ...(text(drawer?.primary_summary) ? { primary_summary: text(drawer?.primary_summary)! } : {}),
      ...(text(drawer?.corroboration_summary) ? { corroboration_summary: text(drawer?.corroboration_summary)! } : {}),
      ...(text(drawer?.context_summary) ? { context_summary: text(drawer?.context_summary)! } : {}),
      ...(text(drawer?.limitation) ? { limitation: text(drawer?.limitation)! } : {}),
    },
    lanes, sources_checked,
    ...(raw.approximate_cost_plan !== undefined
      ? { approximate_cost_plan: normalizeApproximateCostPlan(raw.approximate_cost_plan) } : {}),
    ...(btb ? { btb_breakdown: btb } : {}),
    ...(georgia ? { georgia_observed_evidence: georgia } : {}),
    ...(cohort ? { comparable_cohort: cohort } : {}),
  };
}
